/**
 * FAQ content for the public commons landing page.
 *
 * Used by both:
 *   - `LoginPage.tsx` (renders <details>/<summary> in React)
 *   - `scripts/inject-catalog-plugin.ts` (renders the same content into
 *     index.html at build time so crawlers see Q+A in the literal HTML
 *     without relying on JS execution)
 *
 * Single source of truth keeps the prerendered HTML and the React render
 * in sync; if a copy edit ships in only one place the two paint flows
 * diverge.
 */

export interface FAQ {
  question: string
  answer: string
}

export const FAQS: FAQ[] = [
  {
    question: 'Are these submissions real?',
    answer:
      'Every dossier in CTDCommons is a real IND submission package — either donated by the original sponsor or contributed by a sponsor-investigator with publication rights. Nothing is synthetic, reconstructed from secondary sources, or redacted beyond what the contributor required.',
  },
  {
    question: 'Why is this free?',
    answer:
      'Open regulatory data accelerates research, especially for rare and neglected diseases. CTDCommons exists to give first-time IND sponsors something better to learn from than blank templates, and to give researchers and patient advocates a window into what FDA submission actually looks like.',
  },
  {
    question: 'Who uses CTDCommons?',
    answer:
      'Regulatory affairs leads at small and mid-size biotechs preparing first-in-human INDs, independent regulatory consultants building reference libraries, regulatory affairs trainees learning the eCTD format, and researchers or patient advocates studying real FDA correspondence.',
  },
  {
    question: "What's included in each dossier?",
    answer:
      'The complete eCTD package as submitted: cover letter, Module 1 administrative documents (Forms 1571, 3674), Module 2 summaries, Module 3 CMC, Module 4 nonclinical, and Module 5 clinical. Where contributors have authorized it, FDA correspondence and Pre-IND meeting materials are also included.',
  },
  {
    question: 'How do I read an eCTD submission?',
    answer:
      'The Common Technical Document format organizes a regulatory submission into five modules. Module 1 holds region-specific administrative content. Module 2 contains the high-level summaries written for FDA reviewers. Module 3 covers chemistry, manufacturing, and controls. Modules 4 and 5 hold nonclinical and clinical study reports. Each dossier in CTDCommons is browsable by module so you can jump straight to the section you need.',
  },
  {
    question: 'How do I contribute a dossier?',
    answer:
      'Sponsors and sponsor-investigators interested in donating an IND can contact the CTDCommons team via ctdcommons.org. Contributions can be released under CC-BY-4.0 or under custom terms negotiated with the contributor.',
  },
]
